import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApplicationConfig, provideAppInitializer, inject } from '@angular/core';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { firstValueFrom } from 'rxjs';

import { appRoutes } from './app.routes';
import { environment } from '../environments/environment';
import LavermicoccaPreset from './../assets/themes/lavermicocca';
import { StorageService } from './shared/services/storage.service';
import { provideChunkLoadRecovery } from './shared/utils/chunk-load-error.handler';
import { CustomTranslateLoader } from './shared/utils/custom-translate-loader';
import { Interceptor } from './shared/utils/interceptor';

const SUPPORTED_LANGUAGES = ['it', 'en'];

function resolveLanguage(storage: StorageService): string {
  const savedLanguage = storage.getLanguage();
  return SUPPORTED_LANGUAGES.includes(savedLanguage) ? savedLanguage : 'it';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withEnabledBlockingInitialNavigation()
    ),
    ...provideChunkLoadRecovery(),
    provideHttpClient(withInterceptorsFromDi()),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: LavermicoccaPreset,
        options: { darkModeSelector: '.app-dark' },
      },
      translation: {
        monthNames: [
          'gennaio',
          'febbraio',
          'marzo',
          'aprile',
          'maggio',
          'giugno',
          'luglio',
          'agosto',
          'settembre',
          'ottobre',
          'novembre',
          'dicembre',
        ],
        monthNamesShort: [
          'gen',
          'feb',
          'mar',
          'apr',
          'mag',
          'giu',
          'lug',
          'ago',
          'set',
          'ott',
          'nov',
          'dic',
        ],
        dayNames: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'],
        dayNamesMin: ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'],
        weekHeader: 'Settimana',

        firstDayOfWeek: 1,
      },
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: () => new CustomTranslateLoader('/assets/i18n/', '.json'),
      },
      fallbackLang: 'en',
      lang: resolveLanguage(new StorageService()),
    }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const storage = inject(StorageService);

      translate.addLangs(SUPPORTED_LANGUAGES);
      return firstValueFrom(translate.use(resolveLanguage(storage)));
    }),
    MessageService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: Interceptor,
      multi: true,
    },
  ],
};
